import { useCallback, useMemo } from 'react';
import { useMap } from 'react-use';

import { RecommendedActionStatusEnum } from '@/types/schema';

import { InsightsRecommendationItem, UpdateRecommendationStatusProps } from '../types';
import useUpdateRecommendationStatusMutation from './useUpdateRecommendationStatusMutation';

interface UseRecommendationActionsProps {
  recommendations: InsightsRecommendationItem[];
}

function useRecommendationsActions({ recommendations }: UseRecommendationActionsProps) {
  const [_, { set: setStatus, get: getStatus }] = useMap<
    Record<string, RecommendedActionStatusEnum>
  >({});

  const { updateStatus, isLoading: isUpdatingStatus } = useUpdateRecommendationStatusMutation();

  const updateRecommendationStatus = useCallback(
    async ({ recommendationId, status, snoozeUntil }: UpdateRecommendationStatusProps) => {
      if (
        status === RecommendedActionStatusEnum.Seen &&
        recommendations.find((recommendation) => recommendation.id === recommendationId)?.status !==
          RecommendedActionStatusEnum.New
      )
        return;

      const { currentStatus, error } = await updateStatus({
        recommendedActionIds: [recommendationId],
        status,
        ...(snoozeUntil && { snoozeUntil }),
      });

      if (error || !currentStatus) return;

      setStatus(recommendationId, currentStatus);
    },
    [updateStatus, recommendations, setStatus],
  );

  const updatedRecommendations = useMemo(() => {
    return recommendations.reduce<InsightsRecommendationItem[]>((acc, recommendation) => {
      const updatedStatus = getStatus(recommendation.id);

      if (
        ![RecommendedActionStatusEnum.Dismissed, RecommendedActionStatusEnum.Snoozed].includes(
          updatedStatus,
        )
      ) {
        acc.push({ ...recommendation, status: updatedStatus || recommendation.status });
      }

      return acc;
    }, []);
  }, [recommendations, getStatus]);

  return {
    updatedRecommendations,
    updateRecommendationStatus,
    isUpdatingStatus,
  };
}

export default useRecommendationsActions;
